/**
 * Dead reckoning — nội suy trạng thái máy giữa 2 lần server cập nhật để có
 * "cảm giác realtime" mà không cần server bắn liên tục.
 *
 * Nguyên tắc: `running_time` (phút) từ snapshot là ANCHOR (mốc chuẩn). Giữa các
 * lần poll, FE tự cộng thời gian thực đã trôi kể từ lúc NHẬN snapshot (dùng giờ
 * client). Vì PLC chỉ cho số phút nguyên nên phần giây là ƯỚC LƯỢNG — ta KHÔNG
 * hiển thị đồng hồ mm:ss (dễ nhảy lùi khi reconcile) mà chỉ dùng nó cho thanh
 * progress trườn mượt. Con số phút hiển thị luôn bám anchor, không bao giờ giả.
 */

import type { TeaMachine } from './types'

export interface ElapsedInfo {
    /** Tổng số giây đã chạy (ước lượng, gồm cả phần nội suy). */
    elapsedSec: number
    /** Số phút hiển thị = floor(elapsedSec / 60). Trung thực theo PLC. */
    displayMin: number
    /** Tỉ lệ hoàn thành 0..1 so với `configuredMin`. */
    ratio: number
    /**
     * Đồng hồ FE đã chạm/vượt mức cài nhưng server chưa xác nhận `stopped`.
     * UI hiển thị "đang kết thúc…" và ghim progress ở 100%.
     */
    finishing: boolean
}

const clamp = (n: number, lo: number, hi: number) =>
    Math.min(hi, Math.max(lo, n))

/**
 * Tính thông tin thời gian cho một máy tại thời điểm `nowMs`.
 * Máy không ở trạng thái `running` luôn coi như đứng yên tại `runStartMin`.
 */
export function computeElapsed(
    machine: TeaMachine,
    nowMs: number,
): ElapsedInfo {
    const isRunning = machine.status === 'running'
    const driftedSec = isRunning
        ? Math.max(0, (nowMs - machine.anchorAtMs) / 1000)
        : 0
    const elapsedSec = machine.runStartMin * 60 + driftedSec

    const configuredSec = machine.configuredMin * 60
    const hasTarget = configuredSec > 0
    const finishing = isRunning && hasTarget && elapsedSec >= configuredSec

    const ratio = hasTarget ? clamp(elapsedSec / configuredSec, 0, 1) : 0
    // Ghim số phút không cho vượt mức cài khi đang chờ server chốt.
    const rawMin = Math.floor(elapsedSec / 60)
    const displayMin = hasTarget && finishing ? machine.configuredMin : rawMin

    return { elapsedSec, displayMin, ratio, finishing }
}
